import React, { useRef } from "react";
import PropTypes from "prop-types";

import utils from "@utils";
import { ClientDocumentsService } from "@services";
import { ConfirmationModal } from "../../../components";

import "./UploadFiles.scss";

const UploadFiles = props => {
	const { lang } = global;
	const clientDocInputRef = useRef();
	const confirmationModalRef = useRef();

	const { user, client, clientDocuments, onClientDocumentsChange, dataTestId } = props;

	const removeClientDocument = async documentId => {
		try {
			if (client.Id > 0) {
				await ClientDocumentsService.deleteClientDocument(client.data.Id, documentId);
			}

			onClientDocumentsChange(
				clientDocuments.filter(item => item.CloudFileId !== documentId),
			);
		} catch {
			utils.notify({
				type: "error",
				message: lang.t("Error deleting document"),
			});
		}
	};

	const onDeleteDocument = item =>
		confirmationModalRef.current.openModal({
			title: lang.t("Delete Document Confirmation"),
			message: lang.t("dynamic.Are you sure you want to delete", {
				subject: `${item.FileName}`,
			}),
			confirmButton: lang.t("Yes"),
			onConfirm: () => removeClientDocument(item.CloudFileId),
		});

	const uploadClientDocument = event => {
		event.preventDefault();
		const input = event.target;

		if (!input.files || !input.files[0]) return;
		if (input.files[0].size / (1024 * 1024) > 100) {
			utils.notify({
				message: lang.t("File exceeds the maximum acceptable size"),
				type: "error",
			});
			return;
		}
		const reader = new FileReader();
		reader.readAsDataURL(input.files[0]);
		reader.onload = async () => {
			try {
				const { data: document } = await ClientDocumentsService.uploadClientDocument(
					client.data.Id,
					input.files[0],
				);

				utils.notify({
					type: "success",
					title: lang.t("Upload Successful"),
				});

				onClientDocumentsChange([...clientDocuments, document]);
			} catch {
				utils.notify({
					message: lang.t("Error uploading document"),
					type: "error",
				});
			}
			// value needs to be cleared for reuploading of docs
			input.value = "";
		};
	};

	const clientDocumentClick = () => clientDocInputRef.current.click();

	return (
		<div className="upload-files-input">
			<If condition={clientDocuments.length > 0}>
				<ul className="list-unstyled">
					<For
						each="item"
						of={clientDocuments}
						index="index"
					>
						<li key={`client-document_${item.CloudFileId}`}>
							{utils.formatDate(
								new Date(item.CreateDateUTC),
								"en-US",
								user.TimeZoneId,
								"2-digit",
							)}
							<a
								href={item.Url}
								rel="noopener noreferrer"
								target="_blank"
								aria-label="View Document"
							>
								{item.FileName}
							</a>
							<button
								type="button"
								className="btn btn-link"
								onClick={() => onDeleteDocument(item)}
							>
								<i className="glyphicon glyphicon-trash" />
							</button>
						</li>
					</For>
				</ul>
			</If>
			<div className="button-container">
				<input
					type="file"
					ref={clientDocInputRef}
					accept=".pdf,.jpeg,.jpg,.png,"
					onChange={uploadClientDocument}
					className="hidden"
				/>
				<button
					id="client-document-button"
					data-test-id={dataTestId}
					type="button"
					className="btn btn-default"
					onClick={clientDocumentClick}
				>
					{lang.t("Upload Document")}
				</button>
				<em className="margin-left-15">{`(${lang.t("max size 100MB")})`}</em>
			</div>

			<ConfirmationModal ref={confirmationModalRef} />
		</div>
	);
};

UploadFiles.propTypes = {
	user: PropTypes.object,
	client: PropTypes.object,
	clientDocuments: PropTypes.array.isRequired,
	onClientDocumentsChange: PropTypes.func.isRequired,
	dataTestId: PropTypes.string
};

UploadFiles.defaultProps = {
	user: {},
	client: {},
	dataTestId: undefined
};

export default UploadFiles;
